// EZtoFind.ca — Form translations
// Non-English speakers who chat with Doogie can click through to /buyer, /seller,
// /referral-request, /contact with ?lang=xx and receive a localized form.
// Languages: en, fr, zh-Hant, zh-Hans, pa, fa, pt-PT
// (French forms currently fall back to English text; the Doogie chat still
// replies in French. Full French form translation is a post-launch task.)
// Free-text notes (typed by the visitor in their language) are stored as-is; the
// backend attaches an English-machine-translation via Claude for Doug's CRM.

export const SUPPORTED_LANGS = ["en", "fr", "zh-Hant", "zh-Hans", "pa", "fa", "pt-PT"];

const en = {
  // Common
  "common.submit": "Submit",
  "common.back_home": "Back home",
  "common.thank_you": "Thank you!",
  "common.we_reply_24h": "Doug will reach out within 1 business day.",
  "common.required": "Please complete all required fields including consents.",
  "common.select": "Select…",
  "common.optional": "optional",
  "common.buyer_form": "Buyer Form",
  "common.seller_form": "Seller Form",
  "common.language": "Language",
  // BCFSA notice
  "bcfsa.notice_title": "BCFSA Consumer Notice — Please read before submitting:",
  "bcfsa.notice_body": "Submitting this form does not create a REALTOR®-client relationship. Under the Real Estate Services Rules, Doug LeMaire, REALTOR® will provide you with a formal",
  "bcfsa.dorts_link": "Disclosure of Representation in Trading Services (DoRTS)",
  "bcfsa.notice_after": "before providing real estate services.",
  // Consents
  "consent.casl": "Yes, email me matching listings and market updates from Doug LeMaire, REALTOR®. I can unsubscribe with one click at any time.",
  "consent.casl_optional_note": "Optional — Doug will still respond to this specific request even if you leave this unchecked (CASL s.10(9)(a)).",
  "consent.pipa": "I acknowledge the Privacy Policy (PIPA).",
  "consent.dorts": "I have read the BCFSA Disclosure of Representation in Trading Services and understand my options for representation.",
  // Buyer form
  "buyer.eyebrow": "Buyer Intake",
  "buyer.title": "Tell us what you're looking for",
  "buyer.full_name": "Full Name",
  "buyer.email": "Email",
  "buyer.phone": "Phone",
  "buyer.property_type": "Property Type",
  "buyer.budget_range": "Budget Range",
  "buyer.timeline": "Timeline",
  "buyer.financing_status": "Financing",
  "buyer.preferred_contact": "Preferred Contact",
  "buyer.areas_label": "Areas of interest (BC only)",
  "buyer.areas_placeholder": "e.g. Langley, White Rock, Whistler",
  "buyer.notes": "Notes",
  "buyer.first_time": "I am a first-time home buyer",
  "buyer.under_contract": "I am currently under contract with another REALTOR®",
  "buyer.under_contract_block": "Thank you — but because you're already under contract with another REALTOR®, Doug isn't able to help you directly.",
  "buyer.pt_detached": "Detached",
  "buyer.pt_luxury": "Luxury",
  "buyer.pt_acreage": "Equestrian / Acreage",
  "buyer.pt_estate": "Estate Sale / Probate",
  "buyer.pt_condo": "Condo",
  "buyer.pt_townhouse": "Townhouse",
  "buyer.budget_u750": "Under $750K",
  "buyer.budget_750_1250": "$750K – $1.25M",
  "buyer.budget_1250_2m": "$1.25M – $2M",
  "buyer.budget_2m_3m": "$2M – $3M",
  "buyer.budget_3m_5m": "$3M – $5M",
  "buyer.budget_5mplus": "$5M+",
  "buyer.tl_0_3": "0-3 months",
  "buyer.tl_3_6": "3-6 months",
  "buyer.tl_6_12": "6-12 months",
  "buyer.tl_12plus": "12+ months",
  "buyer.fin_pre": "Pre-approved",
  "buyer.fin_working": "Working on it",
  "buyer.fin_cash": "Cash buyer",
  "buyer.fin_need_info": "Need information",
  "contact.email_pref": "Email",
  "contact.phone_pref": "Phone",
  "contact.text_pref": "Text",
  // Seller form
  "seller.eyebrow": "Seller Intake",
  "seller.title": "Let's talk about your property",
  "seller.city": "City (BC)",
  "seller.property_address": "Property Address",
  "seller.timeline_list": "Timeline to List",
  "seller.estimated_value": "Estimated Value",
  "seller.currently_listed": "The property is currently listed with another REALTOR®",
  "seller.currently_listed_block": "Thank you — but because your property is currently listed with another REALTOR®, Doug isn't able to help you directly.",
  "seller.reason": "Reason for selling",
  "seller.tl_asap": "ASAP",
  "seller.tl_1_3": "1-3 months",
  "seller.tl_3_6": "3-6 months",
  "seller.tl_6_12": "6-12 months",
  "seller.tl_exploring": "Just exploring",
  "seller.ev_u750": "Under $750K",
  "seller.ev_750_1500": "$750K – $1.5M",
  "seller.ev_1500_3m": "$1.5M – $3M",
  "seller.ev_3m_5m": "$3M – $5M",
  "seller.ev_5mplus": "$5M+",
  // Referral
  "ref.eyebrow": "BC-Wide Referral",
  "ref.title": "Referral Request",
  "ref.intro": "Doug's primary practice is Greater Vancouver, Fraser Valley, and Sea-to-Sky. For all other BC communities, if you like, we'll connect you with a REALTOR®.",
  "ref.city": "BC City/Community",
  "ref.city_placeholder": "e.g. Kelowna, Nelson, Prince George",
  "ref.notes": "Anything else we should know?",
  "ref.submit": "Request Referral",
  "ref.success_title": "Referral request received!",
  "ref.success_body": "We'll match you with a REALTOR® active in your area within 1 business day.",
  // Contact
  "contact.eyebrow": "Contact",
  "contact.title": "Get in touch",
  "contact.general": "General",
  "contact.realtors": "REALTORS®",
  "contact.referral_leads": "Referral leads",
};

const zhHant = {
  ...en,
  "common.submit": "提交",
  "common.back_home": "返回首頁",
  "common.thank_you": "多謝！",
  "common.we_reply_24h": "Doug 將於一個工作日內聯絡你。",
  "common.required": "請填妥所有必填欄位（包括同意條款）。",
  "common.select": "請選擇…",
  "common.buyer_form": "買家表格",
  "common.seller_form": "賣家表格",
  "common.language": "語言",
  "bcfsa.notice_title": "BCFSA 消費者通知——遞交前請閱讀：",
  "bcfsa.notice_body": "遞交此表格並不構成 REALTOR®–客戶關係。根據《房地產服務規則》，Doug LeMaire, REALTOR® 會在提供房地產服務前，向你正式提供",
  "bcfsa.dorts_link": "《房地產交易服務代表關係披露》(DoRTS)",
  "bcfsa.notice_after": "文件。",
  "consent.casl": "我同意接收 EZtoFind.ca 的商業電子訊息（CASL），可以隨時取消訂閱。",
  "consent.pipa": "我確認已閱讀《私隱政策》(PIPA)。",
  "consent.dorts": "我已閱讀 BCFSA《房地產代表關係披露》文件，並了解我在代表關係上的選擇。",
  "buyer.eyebrow": "買家登記",
  "buyer.title": "告訴我們你想找甚麼",
  "buyer.full_name": "全名",
  "buyer.email": "電郵",
  "buyer.phone": "電話",
  "buyer.property_type": "物業類型",
  "buyer.budget_range": "預算範圍",
  "buyer.timeline": "時間表",
  "buyer.financing_status": "融資狀況",
  "buyer.preferred_contact": "首選聯絡方式",
  "buyer.areas_label": "感興趣的地區（僅限 BC）",
  "buyer.areas_placeholder": "例如 Langley、White Rock、Whistler",
  "buyer.notes": "備註",
  "buyer.first_time": "我是首次置業人士",
  "buyer.under_contract": "我目前已與另一位 REALTOR® 簽約",
  "buyer.under_contract_block": "感謝——但因為你已與另一位 REALTOR® 簽約，Doug 無法直接為你服務。",
  "buyer.pt_detached": "獨立屋",
  "buyer.pt_luxury": "豪宅",
  "buyer.pt_acreage": "農地／馬房用地",
  "buyer.pt_estate": "遺產出售",
  "buyer.pt_condo": "分契式住宅 (Condo)",
  "buyer.pt_townhouse": "排屋 (Townhouse)",
  "buyer.fin_pre": "已預先批核",
  "buyer.fin_working": "正在處理",
  "buyer.fin_cash": "現金買家",
  "buyer.fin_need_info": "需要資料",
  "contact.email_pref": "電郵",
  "contact.phone_pref": "電話",
  "contact.text_pref": "短訊",
  "seller.eyebrow": "賣家登記",
  "seller.title": "談談你的物業",
  "seller.city": "城市（BC）",
  "seller.property_address": "物業地址",
  "seller.timeline_list": "預計上市時間",
  "seller.estimated_value": "估計價值",
  "seller.currently_listed": "此物業目前已由另一位 REALTOR® 掛牌出售",
  "seller.currently_listed_block": "感謝——但因為你的物業目前已由另一位 REALTOR® 掛牌，Doug 無法直接為你服務。",
  "seller.reason": "出售原因",
  "seller.tl_asap": "盡快",
  "seller.tl_exploring": "只是了解",
  "ref.eyebrow": "全 BC 轉介",
  "ref.title": "轉介請求",
  "ref.intro": "Doug 的主要服務區域為大溫、菲莎河谷及 Sea-to-Sky。BC 其他社區，如您有需要，我們可以為您轉介一位 REALTOR®。",
  "ref.city": "BC 城市／社區",
  "ref.city_placeholder": "例如 Kelowna、Nelson、Prince George",
  "ref.notes": "還有其他想告訴我們的嗎？",
  "ref.submit": "申請轉介",
  "ref.success_title": "已收到轉介申請！",
  "ref.success_body": "我們會於一個工作日內為你配對熟悉該區的 REALTOR®。",
  "contact.eyebrow": "聯絡",
  "contact.title": "與我們聯絡",
  "contact.general": "一般查詢",
  "contact.realtors": "REALTORS®",
  "contact.referral_leads": "轉介客戶",
};

const zhHans = {
  ...zhHant,
  "common.submit": "提交",
  "common.back_home": "返回首页",
  "common.thank_you": "谢谢！",
  "common.we_reply_24h": "Doug 将于一个工作日内联系您。",
  "common.required": "请填写所有必填字段（包括同意条款）。",
  "common.select": "请选择…",
  "common.buyer_form": "买家表格",
  "common.seller_form": "卖家表格",
  "common.language": "语言",
  "bcfsa.notice_title": "BCFSA 消费者通知——提交前请阅读：",
  "bcfsa.notice_body": "提交此表格并不构成 REALTOR®—客户关系。根据《房地产服务规则》，Doug LeMaire, REALTOR® 会在提供房地产服务前，向您正式提供",
  "bcfsa.dorts_link": "《房地产交易服务代表关系披露》(DoRTS)",
  "bcfsa.notice_after": "文件。",
  "consent.casl": "我同意接收 EZtoFind.ca 的商业电子讯息（CASL），可随时退订。",
  "consent.pipa": "我确认已阅读《隐私政策》(PIPA)。",
  "consent.dorts": "我已阅读 BCFSA《房地产代表关系披露》文件，并了解我在代表关系上的选择。",
  "buyer.eyebrow": "买家登记",
  "buyer.title": "告诉我们您想找什么",
  "buyer.full_name": "全名",
  "buyer.email": "邮箱",
  "buyer.phone": "电话",
  "buyer.property_type": "物业类型",
  "buyer.budget_range": "预算范围",
  "buyer.timeline": "时间表",
  "buyer.financing_status": "融资状况",
  "buyer.preferred_contact": "首选联系方式",
  "buyer.areas_label": "感兴趣的地区（仅限 BC）",
  "buyer.first_time": "我是首次置业人士",
  "buyer.under_contract": "我目前已与另一位 REALTOR® 签约",
  "buyer.under_contract_block": "感谢——但因您已与另一位 REALTOR® 签约，Doug 无法直接为您服务。",
  "buyer.pt_detached": "独立屋",
  "buyer.pt_luxury": "豪宅",
  "buyer.pt_acreage": "农地／马房用地",
  "buyer.pt_estate": "遗产出售",
  "buyer.fin_pre": "已预先批核",
  "buyer.fin_working": "正在处理",
  "buyer.fin_cash": "现金买家",
  "buyer.fin_need_info": "需要资料",
  "contact.email_pref": "邮箱",
  "contact.phone_pref": "电话",
  "contact.text_pref": "短信",
  "seller.eyebrow": "卖家登记",
  "seller.title": "谈谈您的物业",
  "seller.city": "城市（BC）",
  "seller.property_address": "物业地址",
  "seller.timeline_list": "预计上市时间",
  "seller.estimated_value": "估计价值",
  "seller.currently_listed": "此物业目前已由另一位 REALTOR® 挂牌出售",
  "seller.currently_listed_block": "感谢——但因您的物业目前已由另一位 REALTOR® 挂牌，Doug 无法直接为您服务。",
  "seller.reason": "出售原因",
  "seller.tl_asap": "尽快",
  "seller.tl_exploring": "只是了解",
  "ref.eyebrow": "全 BC 转介",
  "ref.title": "转介请求",
  "ref.intro": "Doug 的主要服务区域为大温、菲莎河谷及 Sea-to-Sky。BC 其他社区，如您有需要，我们可以为您转介一位 REALTOR®。",
  "ref.city": "BC 城市／社区",
  "ref.city_placeholder": "例如 Kelowna、Nelson、Prince George",
  "ref.notes": "还有其他想告诉我们的吗？",
  "ref.submit": "申请转介",
  "ref.success_title": "已收到转介申请！",
  "ref.success_body": "我们会于一个工作日内为您配对熟悉该区的 REALTOR®。",
  "contact.eyebrow": "联络",
  "contact.title": "与我们联络",
  "contact.general": "一般咨询",
  "contact.realtors": "REALTORS®",
  "contact.referral_leads": "转介客户",
};

const pa = {
  ...en,
  "common.submit": "ਭੇਜੋ",
  "common.back_home": "ਹੋਮ ਪੇਜ ਤੇ ਵਾਪਸ",
  "common.thank_you": "ਸ਼ੁਕਰੀਆ!",
  "common.we_reply_24h": "Doug ਇੱਕ ਕਾਰੋਬਾਰੀ ਦਿਨ ਦੇ ਅੰਦਰ ਸੰਪਰਕ ਕਰੇਗਾ।",
  "common.required": "ਕਿਰਪਾ ਕਰਕੇ ਸਾਰੇ ਲੋੜੀਂਦੇ ਖੇਤਰ ਅਤੇ ਸਹਿਮਤੀਆਂ ਪੂਰੀਆਂ ਕਰੋ।",
  "common.select": "ਚੁਣੋ…",
  "common.buyer_form": "ਖਰੀਦਦਾਰ ਫਾਰਮ",
  "common.seller_form": "ਵਿਕਰੇਤਾ ਫਾਰਮ",
  "common.language": "ਭਾਸ਼ਾ",
  "bcfsa.notice_title": "BCFSA ਖਪਤਕਾਰ ਨੋਟਿਸ — ਭੇਜਣ ਤੋਂ ਪਹਿਲਾਂ ਪੜ੍ਹੋ:",
  "bcfsa.notice_body": "ਇਹ ਫਾਰਮ ਭਰਨ ਨਾਲ REALTOR®–ਗਾਹਕ ਰਿਸ਼ਤਾ ਨਹੀਂ ਬਣਦਾ। Real Estate Services Rules ਅਨੁਸਾਰ, Doug LeMaire, REALTOR® ਸੇਵਾਵਾਂ ਦੇਣ ਤੋਂ ਪਹਿਲਾਂ ਤੁਹਾਨੂੰ ਇੱਕ ਰਸਮੀ",
  "bcfsa.dorts_link": "Disclosure of Representation in Trading Services (DoRTS)",
  "bcfsa.notice_after": "ਦਸਤਾਵੇਜ਼ ਦੇਵੇਗਾ।",
  "consent.casl": "ਮੈਂ EZtoFind.ca ਤੋਂ ਵਪਾਰਕ ਇਲੈਕਟ੍ਰੌਨਿਕ ਸੁਨੇਹੇ ਪ੍ਰਾਪਤ ਕਰਨ ਲਈ ਸਹਿਮਤ ਹਾਂ (CASL). ਮੈਂ ਕਦੇ ਵੀ ਅਨਸਬਸਕ੍ਰਾਈਬ ਕਰ ਸਕਦਾ/ਦੀ ਹਾਂ।",
  "consent.pipa": "ਮੈਂ Privacy Policy (PIPA) ਨੂੰ ਪੜ੍ਹਿਆ ਹੈ।",
  "consent.dorts": "ਮੈਂ BCFSA \"Disclosure of Representation in Trading Services\" ਦਸਤਾਵੇਜ਼ ਪੜ੍ਹਿਆ ਹੈ ਅਤੇ ਪ੍ਰਤੀਨਿਧਤਾ ਦੇ ਵਿਕਲਪ ਸਮਝਦਾ/ਦੀ ਹਾਂ।",
  "buyer.eyebrow": "ਖਰੀਦਦਾਰ ਦਾਖਲਾ",
  "buyer.title": "ਦੱਸੋ ਤੁਸੀਂ ਕੀ ਲੱਭ ਰਹੇ ਹੋ",
  "buyer.full_name": "ਪੂਰਾ ਨਾਮ",
  "buyer.email": "ਈਮੇਲ",
  "buyer.phone": "ਫ਼ੋਨ",
  "buyer.property_type": "ਜਾਇਦਾਦ ਦੀ ਕਿਸਮ",
  "buyer.budget_range": "ਬਜਟ",
  "buyer.timeline": "ਸਮਾਂ-ਸੀਮਾ",
  "buyer.financing_status": "ਵਿੱਤ ਦੀ ਸਥਿਤੀ",
  "buyer.preferred_contact": "ਸੰਪਰਕ ਦੀ ਪਸੰਦ",
  "buyer.areas_label": "ਦਿਲਚਸਪੀ ਦੇ ਖੇਤਰ (ਸਿਰਫ਼ BC)",
  "buyer.notes": "ਟਿੱਪਣੀਆਂ",
  "buyer.first_time": "ਮੈਂ ਪਹਿਲੀ ਵਾਰ ਘਰ ਖਰੀਦ ਰਿਹਾ/ਰਹੀ ਹਾਂ",
  "buyer.under_contract": "ਮੈਂ ਹੁਣ ਕਿਸੇ ਹੋਰ REALTOR® ਨਾਲ ਇਕਰਾਰਨਾਮੇ ਹੇਠ ਹਾਂ",
  "buyer.under_contract_block": "ਧੰਨਵਾਦ — ਪਰ ਕਿਉਂਕਿ ਤੁਸੀਂ ਪਹਿਲਾਂ ਹੀ ਦੂਜੇ REALTOR® ਨਾਲ ਇਕਰਾਰਨਾਮੇ ਹੇਠ ਹੋ, Doug ਸਿੱਧੇ ਤੁਹਾਡੀ ਸਹਾਇਤਾ ਨਹੀਂ ਕਰ ਸਕਦਾ।",
  "buyer.pt_detached": "ਵੱਖਰਾ ਘਰ (Detached)",
  "buyer.pt_luxury": "ਲਗਜ਼ਰੀ",
  "buyer.pt_acreage": "ਏਕੜ / ਘੋੜ-ਸਵਾਰੀ",
  "buyer.pt_estate": "ਅਸਟੇਟ ਸੇਲ / ਪ੍ਰੋਬੇਟ",
  "buyer.pt_condo": "ਕੌਂਡੋ",
  "buyer.pt_townhouse": "ਟਾਊਨਹਾਊਸ",
  "contact.email_pref": "ਈਮੇਲ",
  "contact.phone_pref": "ਫ਼ੋਨ",
  "contact.text_pref": "ਟੈਕਸਟ",
  "seller.eyebrow": "ਵਿਕਰੇਤਾ ਦਾਖਲਾ",
  "seller.title": "ਆਓ ਤੁਹਾਡੀ ਜਾਇਦਾਦ ਬਾਰੇ ਗੱਲ ਕਰੀਏ",
  "seller.city": "ਸ਼ਹਿਰ (BC)",
  "seller.property_address": "ਜਾਇਦਾਦ ਦਾ ਪਤਾ",
  "seller.timeline_list": "ਸੂਚੀਬੱਧ ਕਰਨ ਦੀ ਸਮਾਂ-ਸੀਮਾ",
  "seller.estimated_value": "ਅਨੁਮਾਨਿਤ ਕੀਮਤ",
  "seller.currently_listed": "ਇਹ ਜਾਇਦਾਦ ਪਹਿਲਾਂ ਹੀ ਕਿਸੇ ਹੋਰ REALTOR® ਨਾਲ ਸੂਚੀਬੱਧ ਹੈ",
  "seller.currently_listed_block": "ਧੰਨਵਾਦ — ਪਰ ਕਿਉਂਕਿ ਤੁਹਾਡੀ ਜਾਇਦਾਦ ਪਹਿਲਾਂ ਹੀ ਦੂਜੇ REALTOR® ਨਾਲ ਸੂਚੀਬੱਧ ਹੈ, Doug ਸਿੱਧੀ ਸਹਾਇਤਾ ਨਹੀਂ ਕਰ ਸਕਦਾ।",
  "seller.reason": "ਵੇਚਣ ਦਾ ਕਾਰਨ",
  "ref.eyebrow": "ਪੂਰੀ BC ਲਈ ਰੈਫਰਲ",
  "ref.title": "ਰੈਫਰਲ ਬੇਨਤੀ",
  "ref.intro": "Doug ਦਾ ਮੁੱਖ ਖੇਤਰ Greater Vancouver, Fraser Valley, ਅਤੇ Sea-to-Sky ਹੈ। BC ਦੇ ਹੋਰ ਖੇਤਰਾਂ ਲਈ, ਜੇ ਤੁਸੀਂ ਚਾਹੋ, ਅਸੀਂ ਤੁਹਾਨੂੰ ਇੱਕ REALTOR® ਨਾਲ ਜੋੜ ਸਕਦੇ ਹਾਂ।",
  "ref.city": "BC ਸ਼ਹਿਰ / ਖੇਤਰ",
  "ref.city_placeholder": "ਜਿਵੇਂ Kelowna, Nelson, Prince George",
  "ref.notes": "ਕੀ ਹੋਰ ਕੁਝ ਦੱਸਣਾ ਚਾਹੋਗੇ?",
  "ref.submit": "ਰੈਫਰਲ ਦੀ ਬੇਨਤੀ ਕਰੋ",
  "ref.success_title": "ਰੈਫਰਲ ਬੇਨਤੀ ਪ੍ਰਾਪਤ ਹੋ ਗਈ!",
  "ref.success_body": "ਅਸੀਂ ਇੱਕ ਕਾਰੋਬਾਰੀ ਦਿਨ ਦੇ ਅੰਦਰ ਤੁਹਾਡੇ ਖੇਤਰ ਵਿੱਚ ਸਰਗਰਮ REALTOR® ਨਾਲ ਮੇਲ ਕਰਾਵਾਂਗੇ।",
  "contact.eyebrow": "ਸੰਪਰਕ",
  "contact.title": "ਸੰਪਰਕ ਕਰੋ",
  "contact.general": "ਆਮ",
  "contact.realtors": "REALTORS®",
  "contact.referral_leads": "ਰੈਫਰਲ ਲੀਡ",
};

const fa = {
  ...en,
  "common.submit": "ارسال",
  "common.back_home": "بازگشت به صفحه اصلی",
  "common.thank_you": "متشکریم!",
  "common.we_reply_24h": "داگ ظرف یک روز کاری با شما تماس خواهد گرفت.",
  "common.required": "لطفاً همه فیلدهای الزامی و رضایت‌نامه‌ها را تکمیل کنید.",
  "common.select": "انتخاب کنید…",
  "common.buyer_form": "فرم خریدار",
  "common.seller_form": "فرم فروشنده",
  "common.language": "زبان",
  "bcfsa.notice_title": "اطلاعیه مصرف‌کننده BCFSA — لطفاً پیش از ارسال بخوانید:",
  "bcfsa.notice_body": "ارسال این فرم رابطه REALTOR®–موکل ایجاد نمی‌کند. طبق قوانین Real Estate Services Rules، Doug LeMaire, REALTOR® پیش از ارائه خدمات، یک سند رسمی",
  "bcfsa.dorts_link": "Disclosure of Representation in Trading Services (DoRTS)",
  "bcfsa.notice_after": "به شما ارائه خواهد داد.",
  "consent.casl": "با دریافت پیام‌های الکترونیک تجاری از EZtoFind.ca (CASL) موافقم و می‌توانم هر زمان لغو اشتراک کنم.",
  "consent.pipa": "من سیاست حریم خصوصی (PIPA) را تأیید می‌کنم.",
  "consent.dorts": "من سند BCFSA «افشای نوع نمایندگی در خدمات معاملاتی» را خوانده‌ام و گزینه‌های نمایندگی خود را درک می‌کنم.",
  "buyer.eyebrow": "ثبت‌نام خریدار",
  "buyer.title": "به ما بگویید دنبال چه ملکی هستید",
  "buyer.full_name": "نام کامل",
  "buyer.email": "ایمیل",
  "buyer.phone": "تلفن",
  "buyer.property_type": "نوع ملک",
  "buyer.budget_range": "محدوده بودجه",
  "buyer.timeline": "بازه زمانی",
  "buyer.financing_status": "وضعیت مالی",
  "buyer.preferred_contact": "روش تماس ترجیحی",
  "buyer.areas_label": "مناطق مورد علاقه (فقط BC)",
  "buyer.notes": "توضیحات",
  "buyer.first_time": "من برای اولین بار خانه می‌خرم",
  "buyer.under_contract": "من در حال حاضر با REALTOR® دیگری قرارداد دارم",
  "buyer.under_contract_block": "متشکریم — اما چون شما هم‌اکنون با REALTOR® دیگری قرارداد دارید، داگ مستقیماً نمی‌تواند به شما کمک کند.",
  "buyer.pt_detached": "ویلایی / مستقل",
  "buyer.pt_luxury": "لوکس",
  "buyer.pt_acreage": "زمین بزرگ / اسب‌داری",
  "buyer.pt_estate": "فروش وراثتی",
  "buyer.pt_condo": "کاندو",
  "buyer.pt_townhouse": "تاون‌هاوس",
  "contact.email_pref": "ایمیل",
  "contact.phone_pref": "تلفن",
  "contact.text_pref": "پیامک",
  "seller.eyebrow": "ثبت‌نام فروشنده",
  "seller.title": "درباره ملک شما صحبت کنیم",
  "seller.city": "شهر (BC)",
  "seller.property_address": "آدرس ملک",
  "seller.timeline_list": "زمان‌بندی برای عرضه",
  "seller.estimated_value": "ارزش تخمینی",
  "seller.currently_listed": "این ملک هم‌اکنون با REALTOR® دیگری عرضه شده است",
  "seller.currently_listed_block": "متشکریم — چون ملک شما هم‌اکنون توسط REALTOR® دیگری عرضه شده، داگ مستقیماً نمی‌تواند کمک کند.",
  "seller.reason": "دلیل فروش",
  "ref.eyebrow": "معرفی در سراسر BC",
  "ref.title": "درخواست معرفی",
  "ref.intro": "منطقه اصلی داگ Greater Vancouver, Fraser Valley و Sea-to-Sky است. برای سایر شهرهای BC، در صورت تمایل، می‌توانیم شما را به یک REALTOR® وصل کنیم.",
  "ref.city": "شهر / منطقه در BC",
  "ref.city_placeholder": "مثلاً Kelowna، Nelson، Prince George",
  "ref.notes": "چیز دیگری هست که باید بدانیم؟",
  "ref.submit": "درخواست معرفی",
  "ref.success_title": "درخواست معرفی دریافت شد!",
  "ref.success_body": "ما ظرف یک روز کاری شما را با REALTOR® فعال در منطقه‌تان معرفی می‌کنیم.",
  "contact.eyebrow": "تماس",
  "contact.title": "با ما در تماس باشید",
  "contact.general": "عمومی",
  "contact.realtors": "REALTORS®",
  "contact.referral_leads": "معرفی‌ها",
};

const ptPT = {
  ...en,
  "common.submit": "Enviar",
  "common.back_home": "Voltar à página inicial",
  "common.thank_you": "Obrigado!",
  "common.we_reply_24h": "O Doug entrará em contacto no prazo de 1 dia útil.",
  "common.required": "Por favor preencha todos os campos obrigatórios, incluindo consentimentos.",
  "common.select": "Selecionar…",
  "common.buyer_form": "Formulário de Comprador",
  "common.seller_form": "Formulário de Vendedor",
  "common.language": "Idioma",
  "bcfsa.notice_title": "Aviso ao Consumidor da BCFSA — Por favor leia antes de enviar:",
  "bcfsa.notice_body": "O envio deste formulário não cria uma relação REALTOR®–cliente. Ao abrigo do Real Estate Services Rules, o Doug LeMaire, REALTOR®, fornecer-lhe-á um documento formal de",
  "bcfsa.dorts_link": "Disclosure of Representation in Trading Services (DoRTS)",
  "bcfsa.notice_after": "antes de prestar serviços imobiliários.",
  "consent.casl": "Consinto em receber mensagens eletrónicas comerciais do EZtoFind.ca (CASL). Posso cancelar a subscrição a qualquer momento.",
  "consent.pipa": "Reconheço a Política de Privacidade (PIPA).",
  "consent.dorts": "Li o documento BCFSA \"Disclosure of Representation in Trading Services\" e compreendo as minhas opções de representação.",
  "buyer.eyebrow": "Registo de Comprador",
  "buyer.title": "Diga-nos o que procura",
  "buyer.full_name": "Nome completo",
  "buyer.email": "E-mail",
  "buyer.phone": "Telefone",
  "buyer.property_type": "Tipo de Imóvel",
  "buyer.budget_range": "Intervalo de Orçamento",
  "buyer.timeline": "Prazo",
  "buyer.financing_status": "Financiamento",
  "buyer.preferred_contact": "Contacto Preferido",
  "buyer.areas_label": "Zonas de interesse (apenas BC)",
  "buyer.notes": "Notas",
  "buyer.first_time": "Sou comprador de primeira habitação",
  "buyer.under_contract": "Atualmente estou sob contrato com outro REALTOR®",
  "buyer.under_contract_block": "Obrigado — mas como já está sob contrato com outro REALTOR®, o Doug não pode ajudá-lo diretamente.",
  "buyer.pt_detached": "Moradia (Detached)",
  "buyer.pt_luxury": "Luxo",
  "buyer.pt_acreage": "Rural / Equestre",
  "buyer.pt_estate": "Venda de Herança / Sucessão",
  "buyer.pt_condo": "Condomínio (Condo)",
  "buyer.pt_townhouse": "Casa Geminada (Townhouse)",
  "contact.email_pref": "E-mail",
  "contact.phone_pref": "Telefone",
  "contact.text_pref": "SMS",
  "seller.eyebrow": "Registo de Vendedor",
  "seller.title": "Vamos falar do seu imóvel",
  "seller.city": "Cidade (BC)",
  "seller.property_address": "Endereço do Imóvel",
  "seller.timeline_list": "Prazo para colocar à venda",
  "seller.estimated_value": "Valor estimado",
  "seller.currently_listed": "O imóvel está atualmente à venda por outro REALTOR®",
  "seller.currently_listed_block": "Obrigado — mas como o seu imóvel está atualmente listado por outro REALTOR®, o Doug não pode ajudá-lo diretamente.",
  "seller.reason": "Motivo da venda",
  "ref.eyebrow": "Referência em toda a BC",
  "ref.title": "Pedido de Referência",
  "ref.intro": "A área principal do Doug é Greater Vancouver, Fraser Valley e Sea-to-Sky. Para outras zonas da BC, se quiser, podemos ligá-lo a um REALTOR®.",
  "ref.city": "Cidade/Comunidade em BC",
  "ref.city_placeholder": "por ex. Kelowna, Nelson, Prince George",
  "ref.notes": "Há mais alguma coisa que devamos saber?",
  "ref.submit": "Pedir Referência",
  "ref.success_title": "Pedido de referência recebido!",
  "ref.success_body": "Vamos ligá-lo a um REALTOR® ativo na sua zona no prazo de 1 dia útil.",
  "contact.eyebrow": "Contacto",
  "contact.title": "Fale connosco",
  "contact.general": "Geral",
  "contact.realtors": "REALTORS®",
  "contact.referral_leads": "Referências",
};

const DICTS = {
  en,
  "zh-Hant": zhHant,
  "zh-Hans": zhHans,
  pa,
  fa,
  "pt-PT": ptPT,
};

export function isRTL(lang) {
  return lang === "fa";
}

export function normalizeLang(v) {
  if (!v) return "en";
  const s = String(v).toLowerCase();
  if (s.startsWith("zh-hant") || s === "zh-tw" || s === "zh-hk") return "zh-Hant";
  if (s.startsWith("zh")) return "zh-Hans";
  if (s.startsWith("pa")) return "pa";
  if (s.startsWith("fa")) return "fa";
  if (s.startsWith("pt")) return "pt-PT";
  if (s.startsWith("fr")) return "fr";
  return "en";
}

export function useT(lang) {
  const dict = DICTS[normalizeLang(lang)] || en;
  const t = (key) => dict[key] || en[key] || key;
  return t;
}

// Suffix used to preserve ?lang= across internal navigation
export function langQS(lang) {
  const n = normalizeLang(lang);
  return n === "en" ? "" : `?lang=${encodeURIComponent(n)}`;
}
